import Plot from "react-plotly.js";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";
import { useCallback, useMemo, useState } from "react";
import { Config, Layout, PlotData, PlotMouseEvent } from "plotly.js";
import "../DefectInsights/DefectInsights.css";
import { DefectMetrics } from "../../types/Metric";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectFleetDataItems } from "../../store/selectors/fleetSelectors";
import {
    selectSelectedTechnicalManagers,
    selectSelectedVesselAgeBands,
    selectSelectedVessels,
    selectSelectedVesselTypes,
} from "../../store/selectors/filtersSelectors";
import { ShipData } from "../../types/ShipData";
import { buildRecurringDefectHoverTemplate, mapDefectsMetrics, prepareRecurringDefectMetrics } from "../../utils/DashboardHelper";
import { selectDefectCodes, selectSelectedVessel } from "../../store/selectors/defectSelectors";
import { ClearSelectedVesselButton } from "../Shared/ClearSelectedVesselButton";
import { Button } from "primereact/button";
import { clearAllFilters } from "../../store/thunks/filtersThunks";
import { setSelectedVessel } from "../../store/slices/defectSlice";
import {
    GraphFilterSelector,
    GraphFilterType,
} from "../GraphFilterSelector/GraphFilterSelector";

type RecurringDefectsGraphProps = {
    className?: string;
    data?: Partial<PlotData>[];
    metrics?: DefectMetrics[];
    filterValues: string[] | undefined;
    legendText?: string;
    loading: boolean;
    onClick?: (event: PlotMouseEvent) => void;
    maxItems?: number;
};

export const RecurringDefectsGraph = (props: RecurringDefectsGraphProps) => {
    const {
        className = "defect-insight-graph",
        data,
        metrics,
        filterValues,
        legendText,
        loading,
        onClick,
        maxItems,
    } = props;

    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();

    const [graphFilter, setGraphFilter] = useState<GraphFilterType>(
        GraphFilterType.VESSELS
    );

    const fleetDataItems = useAppSelector(selectFleetDataItems);
    const selectedVessels = useAppSelector(selectSelectedVessels);
    const selectedVesselTypes = useAppSelector(selectSelectedVesselTypes);
    const selectedVesselAgeBands = useAppSelector(selectSelectedVesselAgeBands);
    const selectedTechnicalManagers = useAppSelector(
        selectSelectedTechnicalManagers
    );
    const selectedVessel = useAppSelector(selectSelectedVessel);
    const defectCodes = useAppSelector(selectDefectCodes);

    const vesselMap = useMemo(() => {
        const map = new Map<number, ShipData>();
        fleetDataItems.forEach((item) => {
            const imo = parseInt(item.vessel.imo);
            if (!isNaN(imo)) {
                map.set(imo, item.vessel);
            }
        });
        return map;
    }, [fleetDataItems]);

    const isAgeInBand = (age: number, band: string) => {
        const [min, max] = band.split("-").map(Number);
        return age >= min && age <= max;
    };

    const filteredData = useMemo(() => {
        if (!metrics) {
            return data;
        }

        const hasActiveFilters =
            selectedVessels.length > 0 ||
            selectedVesselTypes.length > 0 ||
            selectedTechnicalManagers.length > 0 ||
            selectedVesselAgeBands.length > 0;

        let filteredMetrics: DefectMetrics[];

        if (hasActiveFilters) {
            filteredMetrics = metrics
                .map((defect) => {
                    const imoNumbers = defect.imo_numbers;

                    if (!imoNumbers) {
                        return defect;
                    }

                    const filteredImos = imoNumbers.filter((imo) => {
                        const vessel = vesselMap.get(imo);
                        if (!vessel) return false;

                        if (
                            selectedVessels.length &&
                            !selectedVessels.includes(vessel.vesselName)
                        ) {
                            return false;
                        }

                        if (
                            selectedVesselTypes.length &&
                            !selectedVesselTypes.includes(vessel.type)
                        ) {
                            return false;
                        }

                        if (
                            selectedTechnicalManagers.length &&
                            !selectedTechnicalManagers.includes(
                                vessel.technicalManager
                            )
                        ) {
                            return false;
                        }

                        if (selectedVesselAgeBands.length) {
                            const age = vessel.shipAge;
                            const inBand = selectedVesselAgeBands.some((band) =>
                                isAgeInBand(age, band)
                            );
                            if (!inBand) return false;
                        }

                        return true;
                    });

                    return {
                        ...defect,
                        imo_numbers: filteredImos,
                    };
                })
                .filter((d) => d.imo_numbers && d.imo_numbers.length > 0);
        } else {
            // Create a shallow copy to avoid mutating the original metrics array
            filteredMetrics = [...metrics];
        }

        // Check if we should show stacked breakdown
        const shouldShowBreakdown =
            (graphFilter === GraphFilterType.VESSELS && selectedVessels.length > 0) ||
            (graphFilter === GraphFilterType.TECHNICAL_MANAGERS && selectedTechnicalManagers.length > 0);

        // If no breakdown needed, return simple bar chart
        if (!shouldShowBreakdown) {
            const metricsWithTotals = prepareRecurringDefectMetrics(filteredMetrics);
            return mapDefectsMetrics(metricsWithTotals, (metric) => 
                buildRecurringDefectHoverTemplate(metric, defectCodes)
            );
        }

        // For stacked breakdown, sort by total occurrences
        filteredMetrics.sort((a, b) => {
            const totalA = a.num_occurrences || 0;
            const totalB = b.num_occurrences || 0;
            return totalB - totalA;
        });

        // Generate stacked data
        const yValues = filteredMetrics.map((m) => {
            const displayName = m.display_name ?? m.type_code;
            return displayName?.length > 40
                ? displayName.substring(0, 40) + "..."
                : displayName;
        });
        const typeCodes = filteredMetrics.map((m) => m.type_code);

        const seriesMap = new Map<string, number[]>();
        const seriesNames: string[] = [];

        filteredMetrics.forEach((metric, index) => {
            const imoNumbers = metric.imo_numbers;
            if (!imoNumbers) return;

            imoNumbers.forEach((imo) => {
                const vessel = vesselMap.get(imo);
                if (!vessel) return;

                let seriesName = "";
                if (graphFilter === GraphFilterType.VESSELS) {
                    seriesName = vessel.vesselName;
                } else {
                    seriesName = vessel.technicalManager;
                }

                if (!seriesMap.has(seriesName)) {
                    seriesMap.set(
                        seriesName,
                        new Array(filteredMetrics.length).fill(0)
                    );
                    seriesNames.push(seriesName);
                }

                const seriesData = seriesMap.get(seriesName)!;
                seriesData[index] += (metric.vessel_occurrences as any)?.[String(imo)] || 0;
            });
        });

        // Sort series names alphabetically or by total count?
        // Alphabetical is usually better for legends
        seriesNames.sort();

        const plotData: Partial<PlotData>[] = seriesNames.map((name) => {
            const xValues = seriesMap.get(name)!;
            
            // Build detailed hover templates for each bar
            const hoverTexts = filteredMetrics.map((metric, index) => {
                const displayName = metric.display_name ?? metric.type_code;
                const matchingCode = defectCodes.find((code) => code.code === metric.type_code);
                const category = matchingCode?.category;
                
                let template = `<b>${displayName}</b>`;
                
                if (category) {
                    template += `<br>Area: ${category}`;
                }
                
                template += `<br>Code: ${metric.type_code}<br>—<br>${name}<br>Occurrences: ${xValues[index]}`;
                
                return template;
            });
            
            return {
                type: "bar",
                orientation: "h",
                name: name,
                x: xValues,
                y: yValues,
                customdata: typeCodes,
                textposition: "inside",
                insidetextanchor: "middle",
                hovertemplate: hoverTexts.map(text => `${text}<extra></extra>`),
            };
        });

        return plotData;
    }, [
        metrics,
        data,
        vesselMap,
        selectedVessels,
        selectedVesselTypes,
        selectedTechnicalManagers,
        selectedVesselAgeBands,
        graphFilter,
        defectCodes,
    ]);

    const limitedData = useMemo(() => {
        if (!maxItems || !filteredData?.length) {
            return filteredData;
        }

        return filteredData.map((trace) => ({
            ...trace,
            x: Array.isArray(trace.x) ? trace.x.slice(0, maxItems) : trace.x,
            y: Array.isArray(trace.y) ? trace.y.slice(0, maxItems) : trace.y,
        }));
    }, [filteredData, maxItems]);

    const getDTickValue = useCallback(() => {
        // Calculate max x value (sum of stacked bars)
        if (filteredData?.length && filteredData[0].y) {
            // We need to sum x values across all traces for the first bar (max)
            // Actually we need the max sum across all bars
            let max = 0;
            if (filteredData[0].y.length > 0) {
                // Assuming all traces have same y length
                const numBars = filteredData[0].y.length;
                for (let i = 0; i < numBars; i++) {
                    let sum = 0;
                    filteredData.forEach((trace) => {
                        if (trace.x && Array.isArray(trace.x)) {
                            const val = trace.x[i] as number;
                            sum += val;
                        }
                    });
                    if (sum > max) max = sum;
                }
            }

            if (max < 11) {
                return 1;
            }
        }

        return undefined;
    }, [filteredData]);

    const layout = useMemo(() => {
        const graphLayout: Partial<Layout> = {
            autosize: true,
            xaxis: {
                showgrid: true,
                gridcolor: "#B1E1E4",
                zerolinecolor: "#B1E1E4",
                fixedrange: true,
                tickfont: {
                    color: "#555",
                    size: 13,
                },
                dtick: getDTickValue(),
                title: "Number of defect occurrences",
                titlefont: {
                    color: "#555",
                    size: 13,
                },
            },
            yaxis: {
                showgrid: false,
                automargin: true,
                autorange: "reversed",
                categoryarray: filterValues?.length ? filterValues : ["All"],
                categoryorder: "total descending",
                fixedrange: true,
                zerolinecolor: "#B1E1E4",
                tickfont: {
                    color: "#555",
                    size: 13,
                },
                titlefont: {
                    color: "#555",
                    size: 13,
                },
                ticklen: 10,
                tickcolor: "rgba(0,0,0,0)",
            },
            margin: {
                pad: 0,
                l: 60,
                t: 20,
                b: 60,
                r: 0,
            },
            font: {
                family: "Open Sans",
                color: "#555",
                size: 12,
            },
            showlegend: !!legendText || !!metrics, // Show legend if we have metrics (stacked)
            legend: {
                title: {
                    font: {
                        color: "#555",
                        size: 13,
                    },
                    text: legendText,
                },
            },
            plot_bgcolor: "#F4F8FB",
            hovermode: "closest",
            hoverlabel: {
                bgcolor: "#1C1940",
                bordercolor: "#1C1940",
                font: {
                    color: "#FFF",
                    size: 13,
                },
            },
            barmode: "stack", // Always stack
        };

        return graphLayout;
    }, [filteredData, filterValues, legendText, getDTickValue, metrics]);

    const config = useMemo(() => {
        const graphConfig: Partial<Config> = {
            displayModeBar: true,
            responsive: true,
            displaylogo: false,
            scrollZoom: false,
            showTips: false,
            modeBarButtonsToRemove: [
                "pan2d",
                "zoom2d",
                "autoScale2d",
                "lasso2d",
                "select2d",
            ],
        };

        return graphConfig;
    }, []);

    const onClickClear = useCallback(() => {
        dispatch(clearAllFilters());
        dispatch(setSelectedVessel(""));
    }, [dispatch]);

    const onFilterChange = useCallback((e: any) => {
        setGraphFilter(e.value);
    }, []);

    const graphFilterOptions = useMemo(
        () => [
            {
                name: t("fleetGraph.graphFilterSelector.vessels"),
                code: GraphFilterType.VESSELS,
            },
            {
                name: t("fleetGraph.graphFilterSelector.technicalManagers"),
                code: GraphFilterType.TECHNICAL_MANAGERS,
            },
        ],
        [t]
    );

    if (loading) {
        return (
            <div className="flex justify-items-center align-items-center">
                <ProgressSpinner />
            </div>
        );
    }

    if (
        !filteredData?.length ||
        !filteredData[0]?.x?.length ||
        !filteredData[0]?.y?.length
    ) {
        return (
            <div className="flex justify-content-center">
                <p>{t("defectsGraph.noDefectsDataAvailable")}</p>
            </div>
        );
    }

    return (
        <>
            {metrics && (
                <div className="flex flex-row justify-content-between mb-4">
                    {selectedVessel ? (
                        <ClearSelectedVesselButton />
                    ) : (
                        <Button
                            id={"clearAllFilters"}
                            className={
                                "p-button p-button-outlined p-button-secondary secondary-outlined-button"
                            }
                            data-cy="clear-all-filters"
                            onClick={onClickClear}
                        >
                            {t("fleetGraph.clear")}
                        </Button>
                    )}
                    <GraphFilterSelector
                        options={graphFilterOptions}
                        onFilterChange={onFilterChange}
                        selectedValue={graphFilter}
                    />
                </div>
            )}
            <Plot
                useResizeHandler={true}
                data={limitedData!!}
                layout={layout}
                config={config}
                className={className}
                onClick={onClick}
            />
        </>
    );
};
